import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Building2, Users, Calendar, Bell, Shield, Mail, Plus, X, Eye, EyeOff, Key, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { CompanySettingsDialog } from '../components/settings/CompanySettingsDialog';

export default function Settings() {
  const navigate = useNavigate();
  const [activeDialog, setActiveDialog] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Role catalogue from backend — single source of truth.
  const [roleCatalogue, setRoleCatalogue] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'hr_manager',
    modules: [],
    modules_overridden: false, // true once admin manually edits the module list
  });
  
  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  
  // Check if user has access (admin or hr_manager)
  const hasAccess = ['admin', 'hr_manager', 'super_user', 'payroll_officer'].includes(user.role);

  const availableModules = [
    { id: 'employees', label: 'EMPLOYEES', color: 'bg-blue-600' },
    { id: 'payroll', label: 'PAYROLL', color: 'bg-emerald-600' },
    { id: 'leave', label: 'LEAVE', color: 'bg-purple-600' },
    { id: 'attendance', label: 'ATTENDANCE', color: 'bg-cyan-600' },
    { id: 'claims', label: 'CLAIMS', color: 'bg-orange-600' },
    { id: 'performance', label: 'PERFORMANCE', color: 'bg-pink-600' },
    { id: 'reports', label: 'REPORTS', color: 'bg-indigo-600' },
    { id: 'settings', label: 'SETTINGS', color: 'bg-slate-600' },
  ];

  useEffect(() => {
    if (hasAccess) {
      fetchAdmins();
      fetchEmployees();
      fetchRoleCatalogue();
    }
  }, [hasAccess]);

  const fetchRoleCatalogue = async () => {
    try {
      const res = await axios.get(`${API}/rbac/roles`);
      setRoleCatalogue(res.data.roles || []);
    } catch (e) {
      // Fallback to a minimal hard-coded list so the form still works if the
      // catalogue endpoint is missing in older deployments.
      setRoleCatalogue([
        { role: 'admin', label: 'Company Admin', default_modules: ['employees', 'leave', 'attendance', 'claims', 'payroll', 'performance', 'reports', 'settings'] },
        { role: 'hr_manager', label: 'HR Manager', default_modules: ['employees', 'leave', 'attendance', 'claims', 'performance', 'reports', 'settings'] },
        { role: 'payroll_officer', label: 'Payroll Officer', default_modules: ['employees', 'payroll', 'reports'] },
        { role: 'manager', label: 'Reporting Manager', default_modules: ['leave', 'attendance', 'claims', 'performance'] },
        { role: 'claims_officer', label: 'Claims / Finance Officer', default_modules: ['claims', 'reports'] },
        { role: 'employee', label: 'Employee', default_modules: ['leave', 'attendance', 'claims'] },
      ]);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/users?role=admin,hr_manager`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch administrators');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.full_name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    emp.position?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  ).slice(0, 5);

  const handleSelectEmployee = (employee) => {
    setFormData({
      ...formData,
      full_name: employee.full_name,
      email: employee.email
    });
    setEmployeeSearchTerm(employee.full_name);
    setShowEmployeeDropdown(false);
  };

  // Apply the role's default modules whenever the role changes, unless the
  // admin has explicitly overridden the module list.
  const handleRoleChange = (newRole) => {
    const entry = roleCatalogue.find((r) => r.role === newRole);
    const defaults = entry?.default_modules || [];
    setFormData((prev) => ({
      ...prev,
      role: newRole,
      modules: prev.modules_overridden ? prev.modules : defaults,
    }));
  };

  const handleAddUser = async () => {
    try {
      await axios.post(`${API}/users`, {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
        modules: formData.modules,
        password: 'Welcome@123',
      });
      toast.success('User added successfully. Password: Welcome@123');
      setShowAddForm(false);
      setEmployeeSearchTerm('');
      fetchAdmins();
      setFormData({
        full_name: '',
        email: '',
        role: 'hr_manager',
        modules: [],
        modules_overridden: false,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const toggleModule = async (userId, moduleId) => {
    // Optimistically update UI
    const updatedAdmins = admins.map(admin => {
      if (admin.user_id === userId) {
        const modules = admin.modules || [];
        const hasModule = modules.includes(moduleId);
        return {
          ...admin,
          modules: hasModule 
            ? modules.filter(m => m !== moduleId)
            : [...modules, moduleId]
        };
      }
      return admin;
    });
    setAdmins(updatedAdmins);

    // Save to backend
    try {
      const admin = updatedAdmins.find(a => a.user_id === userId);
      await axios.patch(`${API}/users/${userId}/modules`, {
        modules: admin.modules
      });
      toast.success('Module access saved!');
    } catch (error) {
      toast.error('Failed to save module access');
      // Revert on error
      fetchAdmins();
    }
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.employee-dropdown')) {
        setShowEmployeeDropdown(false);
      }
      if (!event.target.closest('.module-dropdown')) {
        setShowModuleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleConfigure = (settingType) => {
    if (!hasAccess) {
      toast.error('You do not have permission to modify settings');
      return;
    }
    setActiveDialog(settingType);
  };

  const handleSave = () => {
    toast.success('Settings saved successfully!');
    setActiveDialog(null);
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    // S 25-Jun-2026: client-side guard so the user gets immediate feedback
    // instead of waiting for the backend "must be different" rejection.
    if (passwordData.newPassword === passwordData.currentPassword) {
      toast.error('New password must be different from the current password');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setActiveDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Settings</h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Manage system configuration and preferences</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Company Settings */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Company Settings</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Configure company information, address, and branding
          </p>
          <Button 
            variant="outline" 
            onClick={() => handleConfigure('company')}
            disabled={!hasAccess}
            data-testid="company-settings-configure-btn"
            className="w-full"
          >
            Configure
          </Button>
        </div>

        {/* Iter86 — White-label / Branding (company logo for payslips) */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Branding / Logo</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Upload your company logo to appear on payslips and PDF exports
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/branding')}
            disabled={!hasAccess}
            className="w-full"
            data-testid="branding-settings-card"
          >
            Configure
          </Button>
        </div>

        {/* Leave Types */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Leave Types</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Manage leave types, balances, and policies
          </p>
          <Button 
            variant="outline" 
            onClick={() => handleConfigure('leave')}
            disabled={!hasAccess}
            className="w-full"
          >
            Manage
          </Button>
        </div>

        {/* User Roles */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">User Roles</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Configure user roles and permissions
          </p>
          <Button 
            variant="outline" 
            onClick={() => setShowAddUserDialog(true)}
            disabled={!hasAccess}
            className="w-full"
          >
            Manage
          </Button>
        </div>

        {/* Notifications */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Notifications</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Configure email and system notifications
          </p>
          <Button 
            variant="outline" 
            onClick={() => handleConfigure('notifications')}
            disabled={!hasAccess}
            className="w-full"
          >
            Configure
          </Button>
        </div>

        {/* Security */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
              <Shield className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Security</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Password policies and authentication settings
          </p>
          <Button 
            variant="outline" 
            onClick={() => setActiveDialog('password')}
            className="w-full"
          >
            Change Password
          </Button>
        </div>

        {/* Email Configuration */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Email Configuration</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            SMTP settings and email templates
          </p>
          <Button 
            variant="outline" 
            onClick={() => handleConfigure('email')}
            disabled={!hasAccess}
            className="w-full"
          >
            Configure
          </Button>
        </div>

        {/* Vendor Support - Link to AurousOrbit.ai */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vendor Support</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Manage your optional vendor support subscription.
          </p>
          <Button
            onClick={() => {
              // Iter84 — the vendor support portal URL currently has a
              // broken TLS cert. Falling back to a mailto link until it
              // is provisioned. Flip to `window.open(portalUrl, ...)`
              // once aurousorbit.ai serves valid HTTPS.
              window.location.href = 'mailto:support@aurousorbit.ai?subject=MyHR%20Orbit%20Vendor%20Support%20Request';
            }}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="vendor-support-settings-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Contact Support
          </Button>
        </div>
      </div>

      {!hasAccess && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            ⚠️ You need Admin or HR Manager permissions to modify settings. Contact your administrator for access.
          </p>
        </div>
      )}

      {/* User Management Section */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Manage Admins for All Modules</span>
              <Button
                onClick={() => {
                  setShowAddForm(true);
                  setFormData({ full_name: '', email: '', role: 'hr_manager', modules: [] });
                  setEmployeeSearchTerm('');
                  setShowEmployeeDropdown(false);
                  setShowModuleDropdown(false);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add an Admin
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Users Table */}
            <div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Name</th>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Email</th>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Position</th>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Module</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {admins.map((admin) => (
                      <tr key={admin.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900 dark:text-slate-100">{admin.full_name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-600 dark:text-slate-400">{admin.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700 dark:text-slate-300">{admin.position || admin.role.replace('_', ' ').toUpperCase()}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {availableModules.map((module) => {
                              const hasAccess = (admin.modules || []).includes(module.id);
                              if (!hasAccess) return null;
                              return (
                                <button
                                  key={module.id}
                                  onClick={() => toggleModule(admin.user_id, module.id)}
                                  className={`px-3 py-1 rounded-md text-white text-sm font-medium flex items-center space-x-1 transition-all ${module.color}`}
                                >
                                  <span>{module.label}</span>
                                  <X className="h-3 w-3 ml-1" />
                                </button>
                              );
                            })}
                            {availableModules.filter(m => !(admin.modules || []).includes(m.id)).length > 0 && (
                              <div className="relative inline-block">
                                <button 
                                  className="px-3 py-1 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-400 text-sm font-medium hover:border-emerald-500 hover:text-emerald-600 transition-all"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Toggle dropdown for this specific admin
                                    const btn = e.currentTarget;
                                    const existingDropdown = btn.nextElementSibling;
                                    if (existingDropdown && existingDropdown.classList.contains('module-add-dropdown')) {
                                      existingDropdown.remove();
                                    } else {
                                      // Create dropdown
                                      const dropdown = document.createElement('div');
                                      dropdown.className = 'module-add-dropdown absolute z-50 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg';
                                      dropdown.style.minWidth = '150px';
                                      
                                      availableModules.filter(m => !(admin.modules || []).includes(m.id)).forEach(module => {
                                        const item = document.createElement('button');
                                        item.className = 'w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 transition-colors';
                                        item.textContent = module.label;
                                        item.onclick = (e) => {
                                          e.stopPropagation();
                                          toggleModule(admin.user_id, module.id);
                                          dropdown.remove();
                                        };
                                        dropdown.appendChild(item);
                                      });
                                      
                                      btn.parentElement.appendChild(dropdown);
                                    }
                                  }}
                                >
                                  + Role
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add New Admin Form - Only shown when button clicked */}
            {showAddForm && (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Add New Admin</h3>
                <div className="space-y-4">
                  <div className="relative employee-dropdown">
                    <Label>Search Employee</Label>
                    <div className="relative">
                      <Input
                        value={employeeSearchTerm}
                        onChange={(e) => {
                          setEmployeeSearchTerm(e.target.value);
                          setShowEmployeeDropdown(true);
                        }}
                        onFocus={() => setShowEmployeeDropdown(true)}
                        placeholder="Type to search employee..."
                        className="mt-1"
                      />
                      {showEmployeeDropdown && employeeSearchTerm && filteredEmployees.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {filteredEmployees.map((emp) => (
                            <button
                              key={emp.employee_id}
                              onClick={() => handleSelectEmployee(emp)}
                              className="w-full text-left px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                            >
                              <p className="font-medium text-slate-900 dark:text-slate-100">{emp.full_name}</p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">{emp.email}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative module-dropdown">
                    <Label>Role</Label>
                    <select
                      data-testid="user-role-select"
                      value={formData.role}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                    >
                      {roleCatalogue.map((r) => (
                        <option key={r.role} value={r.role}>{r.label}</option>
                      ))}
                    </select>
                    {(() => {
                      const entry = roleCatalogue.find((r) => r.role === formData.role);
                      if (!entry) return null;
                      return (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {entry.description || ''}
                        </p>
                      );
                    })()}
                  </div>

                  <div className="relative module-dropdown">
                    <div className="flex items-center justify-between">
                      <Label>Module Access</Label>
                      {formData.modules_overridden && (
                        <button
                          type="button"
                          data-testid="reset-modules-default"
                          onClick={() => handleRoleChange(formData.role) || setFormData((p) => ({ ...p, modules_overridden: false }))}
                          className="text-xs text-violet-600 hover:text-violet-700 underline"
                        >
                          Reset to role default
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowModuleDropdown(!showModuleDropdown)}
                      className="w-full mt-1 px-3 py-2 text-left border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 hover:border-slate-400 dark:hover:border-slate-500 transition-colors flex items-center justify-between"
                    >
                      <span className="text-sm">
                        {formData.modules.length === 0 
                          ? 'Select modules...' 
                          : `${formData.modules.length} module(s) selected`}
                      </span>
                      <svg className={`h-4 w-4 transition-transform ${showModuleDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showModuleDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg">
                        {availableModules.map((module) => {
                          const isSelected = (formData.modules || []).includes(module.id);
                          return (
                            <button
                              key={module.id}
                              type="button"
                              onClick={() => {
                                const modules = formData.modules || [];
                                setFormData({
                                  ...formData,
                                  modules: modules.includes(module.id)
                                    ? modules.filter(m => m !== module.id)
                                    : [...modules, module.id],
                                  modules_overridden: true,
                                });
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-b-0 flex items-center justify-between"
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-3 h-3 rounded ${module.color}`}></div>
                                <span className="font-medium text-slate-900 dark:text-slate-100">{module.label}</span>
                              </div>
                              {isSelected && (
                                <svg className="h-5 w-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Selected Modules Display */}
                    {formData.modules.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.modules.map((moduleId) => {
                          const module = availableModules.find(m => m.id === moduleId);
                          return (
                            <span
                              key={moduleId}
                              className={`px-3 py-1 rounded-md text-white text-sm font-medium flex items-center space-x-1 ${module.color}`}
                            >
                              <span>{module.label}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    modules: formData.modules.filter(m => m !== moduleId),
                                    modules_overridden: true,
                                  });
                                }}
                                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowAddForm(false);
                        setFormData({ full_name: '', email: '', role: 'hr_manager', modules: [], modules_overridden: false });
                        setEmployeeSearchTerm('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleAddUser} 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={!formData.email || formData.modules.length === 0}
                    >
                      Add User
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialogs */}
      <CompanySettingsDialog open={activeDialog === 'company'} onClose={() => setActiveDialog(null)} canEdit={['admin', 'hr_manager', 'super_user'].includes(user.role)} />

      <Dialog open={activeDialog === 'leave'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Leave Types</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">Configure annual leave, sick leave, and other leave types</p>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setActiveDialog(null)}>Close</Button>
              <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={activeDialog === 'password'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Key className="h-5 w-5 text-emerald-600" />
              <span>Change Your Password</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Password */}
            <div>
              <Label>Current Password :</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showCurrentPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <Label>New Password :</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showNewPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <Label>Confirm New Password :</Label>
              <div className="relative mt-1">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => {
                setActiveDialog(null);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}>
                Cancel
              </Button>
              <Button onClick={handlePasswordChange} className="bg-emerald-600 hover:bg-emerald-700">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add similar dialogs for other settings as needed */}
    </div>
  );
}
